import { apiClient } from "@/lib/apiClient";
import { MarketTicker_Legacy } from "@/types/models";
import { isUsdInrTicker } from "@/lib/tickerDirection";

const resolveCurrency = (row: any): "USD" | "INR" | "NONE" => {
  if (isUsdInrTicker({ symbol: row?.symbol, name: row?.name })) {
    return "INR";
  }

  const normalized = String(row?.currency || "").toUpperCase();
  if (normalized === "INR") return "INR";
  if (normalized === "NONE") return "NONE";
  return "USD";
};

const fromApi = (row: any): MarketTicker_Legacy => ({
  id: row.id,
  symbol: row.symbol,
  name: row.name,
  currency: resolveCurrency(row),
  price: Number(row.price),
  change: Number(row.change),
  changePercent: Number(row.change_percent),
  lastUpdated: row.last_updated,
});

const toApi = (row: Partial<MarketTicker_Legacy>) => ({
  ...(row.id !== undefined ? { id: row.id } : {}),
  ...(row.symbol !== undefined ? { symbol: row.symbol } : {}),
  ...(row.name !== undefined ? { name: row.name } : {}),
  ...(row.currency !== undefined ? { currency: row.currency } : {}),
  ...(row.price !== undefined ? { price: row.price } : {}),
  ...(row.change !== undefined ? { change: row.change } : {}),
  ...(row.changePercent !== undefined ? { change_percent: row.changePercent } : {}),
  ...(row.lastUpdated !== undefined ? { last_updated: row.lastUpdated } : {}),
});

class MockTickerService {
  async getAll(): Promise<MarketTicker_Legacy[]> {
    const rows = await apiClient.get<any[]>("/mock-ticker-data");
    return rows.map(fromApi);
  }

  async updateAll(tickers: MarketTicker_Legacy[]): Promise<void> {
    await apiClient.put<any[]>("/mock-ticker-data/replace", {
      tickers: tickers.map((ticker) => ({
        ...toApi(ticker),
        last_updated: ticker.lastUpdated || new Date().toISOString(),
      })),
    });
  }

  async update(id: string, ticker: Partial<MarketTicker_Legacy>): Promise<MarketTicker_Legacy> {
    const updated = await apiClient.put<any>(`/mock-ticker-data/${id}`, {
      ...toApi(ticker),
      last_updated: ticker.lastUpdated || new Date().toISOString(),
    });

    return fromApi(updated);
  }

  async add(ticker: MarketTicker_Legacy): Promise<MarketTicker_Legacy> {
    const created = await apiClient.post<any>("/mock-ticker-data", {
      ...toApi(ticker),
      id: ticker.id,
      last_updated: ticker.lastUpdated || new Date().toISOString(),
    });

    return fromApi(created);
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete<void>(`/mock-ticker-data/${id}`);
  }
}

export const mockTickerService = new MockTickerService();
